import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import StatusChip from "../StatusChip";

const SENTIMENT = {
  positivo: { label: "Positivo", tone: "success" },
  neutro: { label: "Neutro", tone: "neutral" },
  negativo: { label: "Negativo", tone: "danger" },
  frustrado: { label: "Frustrado", tone: "danger" },
};

const useStyles = makeStyles((theme) => ({
  block: { marginBottom: theme.spacing(2) },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    display: "block",
    marginBottom: 4,
  },
  text: { fontSize: 14, lineHeight: 1.55 },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(5, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

/**
 * Resumo do atendimento para quem vai assumir a conversa.
 *
 * Busca ao abrir e serve do cache quando não houve mensagem nova — o rótulo
 * "em cache" existe para o atendente saber que está lendo algo de antes e
 * poder regerar, em vez de desconfiar em silêncio.
 */
const ConversationSummaryModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (force = false) => {
      if (!ticketId) return;
      setLoading(true);
      try {
        const { data: result } = await api.post(
          `/ai-assist/tickets/${ticketId}/summary`,
          { force }
        );
        setData(result);
      } catch (err) {
        toastError(err);
        onClose();
      } finally {
        setLoading(false);
      }
    },
    [ticketId, onClose]
  );

  useEffect(() => {
    if (open) load(false);
    else setData(null);
  }, [open, load]);

  const sentiment = SENTIMENT[data?.sentiment] || SENTIMENT.neutro;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resumo do atendimento</DialogTitle>
      <DialogContent>
        {loading && !data && (
          <div className={classes.loading}>
            <CircularProgress size={28} />
            <Typography variant="body2">Lendo a conversa…</Typography>
          </div>
        )}

        {data && (
          <>
            <div className={classes.block}>
              <span className={classes.label}>O que aconteceu</span>
              <div className={classes.text}>{data.summary}</div>
            </div>
            {data.wants && (
              <div className={classes.block}>
                <span className={classes.label}>O que o cliente quer</span>
                <div className={classes.text}>{data.wants}</div>
              </div>
            )}
            {data.pending && (
              <div className={classes.block}>
                <span className={classes.label}>Pendente</span>
                <div className={classes.text}>{data.pending}</div>
              </div>
            )}

            <div className={classes.meta}>
              <StatusChip dot tone={sentiment.tone} label={sentiment.label} />
              <Typography variant="caption">
                {data.messageCount} mensagens analisadas
              </Typography>
              {data.cached && (
                <Typography variant="caption">
                  · em cache, sem mensagens novas desde a última geração
                </Typography>
              )}
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => load(true)}
          disabled={loading}
        >
          Gerar de novo
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

ConversationSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ticketId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ConversationSummaryModal.defaultProps = { ticketId: null };

export default ConversationSummaryModal;
